import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';
import { LanguagesService } from '../services/languages.service';

@Component({
    selector: 'platform-pagination',
    standalone: true,
    imports: [FormsModule, DropdownModule, ButtonModule, PUITranslatePipe],
    template: `
        <div class="pagination-container flex w-full">
            <div class="flex pagination gap-16 align-items-center">
                <span class="text-sm">{{ showByLabel | pTranslate }}</span>
                <p-dropdown
                    [options]="pageSizeOptions"
                    [(ngModel)]="selectedPageSize"
                    optionLabel="viewValue"
                    optionValue="value"
                    [dropdownIcon]="dropdownIconClass"
                    (onShow)="onDropdownOpen.emit()"
                    (onHide)="onDropdownClose.emit()"
                    (onChange)="onPageSizeChange($event)"
                    styleClass="dropdown w-min padding-py-1 text-sm line-height-1 padding-px-2 text-medium"
                ></p-dropdown>

                <div class="flex align-items-center gap-8-px text-sm">
                    <i
                        [class]="currentPage > 1 ? 'pi pi-angle-left cursor-pointer' : 'pi pi-angle-left disabled'"
                        (click)="goToPreviousPage()"
                    ></i>

                    <span class="text-medium">{{ getDisplayedRange() }}</span>
                    <span class="pagination-count">{{ ofLabel | pTranslate }}</span>
                    <span class="text-normal pagination-count">{{ totalItems }}</span>

                    <i
                        [class]="hasNextPage() ? 'pi pi-angle-right cursor-pointer' : 'pi pi-angle-right disabled'"
                        (click)="goToNextPage()"
                    ></i>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .pagination-container {
                font-family: 'IBM Plex Sans', sans-serif;
                color: var(--alpha-text-color);
                background: var(--alpha-base-light-color);
            }

            .pagination-count {
                color: var(--alpha-sidebar-header-text-color);
            }

            .cursor-pointer {
                cursor: pointer;
            }

            .disabled {
                opacity: 0.5;
                pointer-events: none;
            }
        `,
    ],
})
export class PaginationComponent implements OnChanges {
    @Input() pageSizeOptions: { value: number; viewValue: string }[] = [];
    @Input() totalItems: number = 0;
    @Input() currentPage: number = 1;
    @Input() position: 'left' | 'right' = 'right';
    @Input() showByLabel: string = 'default-cm-showBy';
    @Input() ofLabel: string = 'default-cm-of';
    @Input() dropdownIconClass: string = 'pi pi-chevron-down';
    @Input() defaultPageSize: number | null = null;

    @Output() pageChange = new EventEmitter<number>();
    @Output() pageSizeChange = new EventEmitter<number>();
    @Output() onDropdownOpen = new EventEmitter<void>();
    @Output() onDropdownClose = new EventEmitter<void>();

    @Input() selectedPageSize: number = 10;

    languagesService = inject(LanguagesService);
    destroyRef = inject(DestroyRef);

    getPageSizeOpts() {
        const rowTranslation = this.languagesService.getTranslation('default-cm-rows') || 'Rows';
        this.pageSizeOptions = this.pageSizeOptions.map((opts) => ({
            ...opts,
            viewValue: `${opts.value} ${rowTranslation}`,
        }));
    }

    ngOnInit() {
        this.languagesService.selectedLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((language) => {
            if (language) {
                this.getPageSizeOpts();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['pageSizeOptions'] && this.pageSizeOptions.length > 0) {
            // Set default page size if provided, or fall back to the first option
            const defaultSize = this.defaultPageSize || this.pageSizeOptions[0].value;
            this.selectedPageSize = this.pageSizeOptions.some((opt) => opt.value === defaultSize)
                ? defaultSize
                : this.pageSizeOptions[0].value;
        }
    }

    goToPreviousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.pageChange.emit(this.currentPage);
        }
    }

    goToNextPage() {
        if (this.hasNextPage()) {
            this.currentPage++;
            this.pageChange.emit(this.currentPage);
        }
    }

    onPageSizeChange(event: any) {
        const newPageSize = event?.value || this.pageSizeOptions[0]?.value || 10;
        const actualEmittedValue = event?.value || this.pageSizeOptions[0]?.value || 10;
        if (newPageSize > 0) {
            this.selectedPageSize = actualEmittedValue;
            this.currentPage = 1; // Reset to the first page
            this.pageSizeChange.emit(this.selectedPageSize);
            this.pageChange.emit(this.currentPage);
        }
    }

    hasNextPage(): boolean {
        const validPageSize = this.selectedPageSize || 10;
        return this.currentPage < Math.ceil(this.totalItems / validPageSize);
    }

    getDisplayedRange(): string {
        const validPageSize = this.selectedPageSize || 10;
        const start = (this.currentPage - 1) * validPageSize + 1;
        const end = Math.min(start + validPageSize - 1, this.totalItems);
        return this.totalItems > 0 ? `${start} - ${end}` : '0';
    }
}
